// Single source of truth for the public-facing network metrics that appear in
// multiple components (HeroSection, CaseStudies, TrustedBy, etc.).
//
// Background: the audit found that the same metric was being maintained in two
// or three places — hero showed 25 ms to Frankfurt while CaseStudies hardcoded
// 27 ms with the label "Измерено latency" (ungrammatical English-Bulgarian
// hybrid). Consolidating here removes the drift surface and makes the i18n
// labels consistent.
//
// This module is server-safe (no React imports), so Server Components can read
// from it during SSR without forcing the i18n client bundle.

import { ALL_POP_IDS } from "@/data/service-availability";
import { ALL_POPS, INTERNATIONAL_POPS } from "@/lib/network-data";
import { SEGMENTS } from "@/data/network-paths";
import type { Language } from "./i18n-server";
export type LocalizedLabel = { en: string; bg: string };

export type NetworkStat = {
  /** Raw display value, already formatted with its unit. */
  value: string;
  /** Optional numeric value if downstream code needs to compute something. */
  numeric?: number;
  /** Unit suffix when value is numeric (e.g. "ms", "%"). */
  unit?: string;
  /** Localized label for the metric. */
  label: LocalizedLabel;
};

/**
 * Hero "Sofia → Frankfurt" latency, round trip (RTT), conservative upper bound.
 * Earlier docs called these figures one-way ("RTT/2"); that cannot hold —
 * 25 ms one-way is a 50 ms RTT, worse than the ~30 ms public-internet ping
 * for the same city pair. All corridor figures are RTT; see
 * src/data/network-paths.ts.
 * If this changes, update both /tools/latency expected baseline and the case
 * study under Content Platform (currently sharing the same 25 ms figure).
 */
export const SOFIA_FRANKFURT_LATENCY_MS = 25;

/**
 * Route-level latency figures used by the redesigned homepage hero proof
 * points. These come from the same source that UnifiedConfigurator reads
 * its LATENCY_MATRIX from (src/components/UnifiedConfigurator.tsx, matrix
 * key `sofia`) — the hero must not drift from what the configurator quotes
 * when a user actually picks the route.
 *
 * We keep these as separate exports (rather than repurposing
 * SOFIA_FRANKFURT_LATENCY_MS) because the older constant is documented as
 * a "conservative upper bound" used by CaseStudies and TrustedBy, whose
 * copy explicitly reads "25 ms". Renaming or lowering that value would
 * ripple into components this PR is not allowed to touch.
 *
 * Literals on purpose: scripts/generate-llms-txt.mjs parses them. They must
 * equal rttFromSofia() in src/data/network-paths.ts — enforced by
 * src/__tests__/network-paths.test.mjs.
 */
export const HERO_SOFIA_FRANKFURT_LATENCY_MS = 24.5;
export const HERO_SOFIA_ISTANBUL_LATENCY_MS = 12;

// Legacy alias kept because HeroHeader was drafted against these names before
// the split above was formalized. Prefer the HERO_* names in new code.
export const SOFIA_ISTANBUL_LATENCY_MS = HERO_SOFIA_ISTANBUL_LATENCY_MS;

/** Network SLA tier offered to enterprise wavelengths customers. */
export const ENTERPRISE_SLA_PERCENT = "99.999%";

/** Headline corporate uptime metric (lower tier, advertised on hero). */
export const CORPORATE_UPTIME_PERCENT = "99.99%";

// ─── Per-service SLA tiers ────────────────────────────────────────────
// Authoritative values. If a prose surface renders a different figure,
// update the surface — do not shadow this table.

/** IP Transit SLA percent, rendered as "99.99%". */
export const IP_TRANSIT_SLA_PERCENT = "99.99%";

/** DWDM Wavelengths SLA percent, rendered as "99.999%". Alias of ENTERPRISE_SLA_PERCENT. */
export const DWDM_SLA_PERCENT = ENTERPRISE_SLA_PERCENT;

/** Ethernet / L2VPN SLA percent, rendered as "99.9%". */
export const ETHERNET_L2VPN_SLA_PERCENT = "99.9%";

/** Dark Fiber SLA percent, rendered as "99.9%". */
export const DARK_FIBER_SLA_PERCENT = "99.9%";

// ─── Network identity ────────────────────────────────────────────────

/** Sofia Connect's ASN, rendered as "AS47872" or "47872" depending on context. */
export const SOFIA_CONNECT_ASN = 47872;

/** Formatted ASN string (with "AS" prefix) for prose surfaces. */
export const SOFIA_CONNECT_ASN_LABEL = `AS${SOFIA_CONNECT_ASN}` as const;

/** BGP full-table prefix count advertised on IP Transit surfaces, rendered as "900K+". */
export const BGP_PREFIX_COUNT_LABEL = "900K+" as const;

/**
 * Floor value for BGP peer count. Used as a fallback if the live
 * PeeringDB lookup in /api/network fails or is stale. The live count
 * may be higher; this is the minimum we are willing to advertise.
 */
export const BGP_PEER_FLOOR = 96;

/**
 * Fallback prose label for BGP peer count when a live number is not
 * available at render time. Live surfaces should call
 * `formatPeerCount(livePeers)` instead.
 */
export const BGP_PEER_FALLBACK_LABEL = `${BGP_PEER_FLOOR}+` as const;

/**
 * Format a peer count for prose. If `live` is undefined or less than
 * the floor, render "96+" (never overstate downwards or upwards).
 * If `live` is >= the floor, render the exact live number.
 */
export function formatPeerCount(live?: number | null): string {
  if (typeof live !== "number" || !Number.isFinite(live) || live < BGP_PEER_FLOOR) {
    return BGP_PEER_FALLBACK_LABEL;
  }
  return String(live);
}

// ─── Points of Presence ──────────────────────────────────────────────

/**
 * Total PoP count, derived from the authoritative ALL_POP_IDS list.
 * Named TOTAL_POP_COUNT to avoid a name collision with the richer
 * POP_COUNT object (`{ total, international, domestic }`) in
 * `@/lib/network-data`. Both should agree on the total number.
 */
export const TOTAL_POP_COUNT = ALL_POP_IDS.length;

/** Formatted PoP count string, e.g. "18". */
export const POP_COUNT_LABEL = String(TOTAL_POP_COUNT);

// ─── Countries served ───────────────────────────────────────

/**
 * PoPs reached only through a long-haul partner: every route segment that
 * touches them is a "partner" segment in the route model (today: Dubai).
 */
export const PARTNER_POP_IDS: readonly string[] = ALL_POPS.filter((p) => {
  const segs = SEGMENTS.filter((s) => s.a === p.id || s.b === p.id);
  return segs.length > 0 && segs.every((s) => s.medium === "partner");
}).map((p) => p.id);

/**
 * Countries on Sofia Connect's own network, derived from the PoP list
 * minus partner-reached PoPs. Owner decision (21 Oct 2026): Dubai is
 * reached through a partner and does not count, so this is 7, not 8.
 * Sentences that talk about all PoPs mention the partner reach separately.
 */
export const COUNTRIES_SERVED_COUNT = new Set(
  ALL_POPS.filter((p) => !PARTNER_POP_IDS.includes(p.id)).map((p) => p.country),
).size;

/** Formatted country count string, e.g. "7". */
export const COUNTRIES_SERVED_LABEL = String(COUNTRIES_SERVED_COUNT);

/** International carrier PoPs only (the corridor map on the homepage). */
export const INTERNATIONAL_POP_COUNT_LABEL = String(INTERNATIONAL_POPS.length);

/**
 * "15+ Countries with customers" counts countries where customers are served, which
 * is wider than COUNTRIES_SERVED_COUNT (countries on our own network).
 * Confirmed by the owner on 21 Oct 2026; keep both.
 */
export const COUNTRIES_SERVED_CLAIM = "15+";

// ─── Latency label helpers ───────────────────────────────────────────
// Format helpers preserve the exact rendering used on the site today.
// If you need a new format, add a helper — don't sprinkle template
// literals across components.

/**
 * Round to at most one decimal for display. Live values from
 * /api/metrics/ arrive as raw floats (e.g. 24.929261528047476); constants
 * such as 24.5 or 12 pass through unchanged.
 */
export function roundLatency(ms: number): number {
  return Math.round(ms * 10) / 10;
}

/** "25 ms" (spec cards, upper-bound). */
export function formatLatencyMs(ms: number): string {
  return `${roundLatency(ms)} ms`;
}

/** "~25ms" (spec-card style, no space). */
export function formatLatencyApproxNoSpace(ms: number): string {
  return `~${roundLatency(ms)}ms`;
}

/**
 * Round-trip latency headline for a specific corridor, e.g. "~24.5 ms RTT".
 * Always spell out "RTT" so readers can compare like with like.
 * (Function name kept for existing call sites.)
 */
export function formatLatencyOneWay(ms: number): string {
  return `~${roundLatency(ms)} ms RTT`;
}

/**
 * Localized round-trip latency label, e.g. `~24.5 ms RTT` (en) /
 * `~24.5 ms RTT` (bg). The HERO_* values are round-trip times (see
 * src/data/network-paths.ts); they were previously mislabelled one-way.
 */
export function formatLatencyRttLabel(ms: number, lang: Language): string {
  const v = roundLatency(ms);
  void lang;
  return `~${v} ms RTT`;
}

/**
 * Corridor-scoped latency copy for the three sales tools (SolutionFinder,
 * ROICalculatorClient, NetworkAdvisorClient). Sources the numeric value
 * from HERO_SOFIA_FRANKFURT_LATENCY_MS so any drift is caught by a single
 * edit.
 *
 * Wholesale-audience English only — the sales tools are English-only
 * surfaces today. If either tool gains bg localisation, add a bg branch
 * here rather than sprinkling literals across the tool.
 */
export const CORRIDOR_LATENCY_LABELS = {
  sofiaFrankfurt: `~${HERO_SOFIA_FRANKFURT_LATENCY_MS} ms RTT Sofia–Frankfurt`,
  sofiaIstanbul: `~${HERO_SOFIA_ISTANBUL_LATENCY_MS} ms RTT Sofia–Istanbul`,
} as const;

/** Bulgarian mirror of CORRIDOR_LATENCY_LABELS (REVIEW-BG draft). */
export const CORRIDOR_LATENCY_LABELS_BG = {
  sofiaFrankfurt: `~${HERO_SOFIA_FRANKFURT_LATENCY_MS} ms RTT София–Франкфурт`,
  sofiaIstanbul: `~${HERO_SOFIA_ISTANBUL_LATENCY_MS} ms RTT София–Истанбул`,
} as const;

/** Stats displayed in the TrustedBy section. */
export const TRUSTED_BY_STATS: NetworkStat[] = [
  {
    value: "50+",
    label: { en: "Active Clients", bg: "Активни клиенти" },
  },
  {
    value: COUNTRIES_SERVED_CLAIM,
    label: { en: "Countries with customers", bg: "Държави с клиенти" },
  },
  {
    value: CORPORATE_UPTIME_PERCENT,
    label: { en: "Network Uptime", bg: "Достъпност на мрежата" },
  },
  {
    value: "24/7",
    label: { en: "NOC Support", bg: "NOC поддръжка" },
  },
];

/** Resolve a localized label for a `LocalizedLabel`. */
export function pickLabel(label: LocalizedLabel, lang: Language): string {
  return lang === "bg" ? label.bg : label.en;
}
